import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';

import { AppSelectors } from 'src/app/store/app/app.selectors';
import { ModalController } from '@ionic/angular';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import _ from 'lodash';

import {
  ModifierPromptOption,
  ModifierPromptOptionType,
  POSModifierPromptAssignment
} from 'src/app/models/pos-manager.model';
import { BaseMenuItem, MenuItem } from 'src/app/models';
import { MenuSelectors } from 'src/app/store/menu/menu.selector';
import { ModalService } from '../../services';
import { IsOptionSelectedPipe } from '../../pipes/is-option-selected-pipe/is-option-selected-pipe.pipe';

const NO_PROMPTS_FOUND = -1;

@Component({
  selector: 'pos-item-modifier-prompts-modal',
  templateUrl: './item-modifier-prompts.modal.html',
  styleUrls: ['./item-modifier-prompts.modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddItemModifierPromptsModal implements OnInit {
  @ViewSelectSnapshot(AppSelectors.getSelectedLocaleCode)
  currentLang: string;

  @Input() item: BaseMenuItem;
  readonly OptionType = ModifierPromptOptionType;

  @Input() isSelfService = false;

  @SelectSnapshot(MenuSelectors.getFullPromptSetup)
  private getFullPromptByMenuItem: (menuItem: BaseMenuItem) => MenuItem;
  @ViewChildren('warningTarget') warningTargets: QueryList<ElementRef>;

  isFormValid: boolean;
  invalidPromptIndex?: number;
  openedAccordions: string[] = [];
  selectedPrompts: POSModifierPromptAssignment[] = [];

  readonly faTimes = faTimes;
  readonly faExclamationCircle = faExclamationCircle;
  readonly NO_PROMPTS_FOUND = NO_PROMPTS_FOUND;

  constructor(
    private cdr: ChangeDetectorRef,
    private readonly modalController: ModalController,
    private modalService: ModalService
  ) {}

  ngOnInit() {
    if (this.item && this.item.modifier_prompt_assignments?.length) {
      const modifier_prompt_assignments = this.getFullPromptByMenuItem(
        this.item
      )?.modifier_prompt_assignments;

      if (!modifier_prompt_assignments?.length) {
        return;
      }

      this.openedAccordions = modifier_prompt_assignments.map((x) =>
        x.id.toString()
      );

      this.selectedPrompts = modifier_prompt_assignments.map((x) => ({
        ...x,
        selectedPrompts: [],
        isValid: this.modalService.isValidPrompt(x)
      }));
      this.checkIsFormValid();
    } else {
      this.openedAccordions = [];
      this.selectedPrompts = [];
      this.isFormValid = true;
    }
  }

  /**
   * Check if the prompt is valid based on min and max values and selected options
   */
  async onSave() {
    this.invalidPromptIndex = this.findInvalidPrompt();
    if (this.invalidPromptIndex && this.invalidPromptIndex === -1) {
      const prompts = _.flatMap(this.selectedPrompts, (p) =>
        p.selectedOptions?.length
          ? _.orderBy(p.selectedOptions, 'sort_order')
          : []
      );
      await this.modalController.dismiss({
        menuItem: _.cloneDeep(this.item),
        options: prompts
      });
    } else {
      this.cdr.detectChanges();
      await this.scrollToError();
    }
  }

  onSelect(prompt: POSModifierPromptAssignment, option: ModifierPromptOption) {
    this.modalService.onSelect(this.selectedPrompts, prompt, option);
    this.checkIsFormValid();
  }

  async onClose() {
    await this.modalController.dismiss();
  }

  private checkIsFormValid() {
    this.isFormValid = this.modalService.checkIsFormValid(this.selectedPrompts);
  }

  async scrollToError() {
    const firstWarning = this.warningTargets.first;
    if (firstWarning) {
      firstWarning.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }

  private findInvalidPrompt() {
    if (!this.modalService.checkIsFormValid(this.selectedPrompts)) {
      return this.selectedPrompts.findIndex((x) => !x.isValid);
    }
    return NO_PROMPTS_FOUND;
  }

  isOptionSelected(
    option: ModifierPromptOption,
    selectedOptions?: ModifierPromptOption[]
  ) {
    return selectedOptions?.some((x) => x.id === option.id);
  }

  protected readonly IsOptionSelectedPipe = IsOptionSelectedPipe;
}
